import React, { useState, useEffect } from 'react';
import { X, Smartphone, Download, Share, Plus } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Mobile device detection
  const detectDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check if already in standalone mode
    const standalone = window.navigator.standalone || 
                      window.matchMedia('(display-mode: standalone)').matches ||
                      document.referrer.includes('android-app://');
    
    // iOS detection
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    
    // Android detection
    const android = /android/i.test(userAgent);
    
    setIsIOS(iOS);
    setIsAndroid(android);
    setIsStandalone(standalone);
    
    return { iOS, android, standalone };
  };

  // Check if user has already dismissed the prompt
  const hasUserDismissed = () => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
    
    if (!dismissed) return false;
    
    // Show again after 7 days if user clicked "Maybe Later"
    if (dismissed === 'maybe-later' && dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      return daysSinceDismissed < 7;
    }
    
    // Never show again if user clicked "Add Now" or permanently dismissed
    return dismissed === 'installed' || dismissed === 'never';
  };

  // Handle beforeinstallprompt event (Android/Chrome)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 PWA: beforeinstallprompt event fired');
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
    };

    // Listen for the install prompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Check if we should show the install prompt
  useEffect(() => {
    const { iOS, android, standalone } = detectDevice();
    
    console.log('📱 PWA Install Check:', {
      iOS,
      android,
      standalone,
      hasUserDismissed: hasUserDismissed(),
      deferredPrompt: !!deferredPrompt
    });

    // Don't show if already installed or user dismissed
    if (standalone || hasUserDismissed()) {
      return;
    }

    // Show for iOS Safari (not in standalone mode)
    if (iOS && !standalone) {
      setShowPrompt(true);
      return;
    }

    // Show for Android with beforeinstallprompt support
    if (android && deferredPrompt) {
      setShowPrompt(true);
      return;
    }
  }, [deferredPrompt]);

  // Handle Android install
  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;

    try {
      // Show the install prompt
      const result = await deferredPrompt.prompt();
      console.log('📱 PWA: Install prompt result:', result);

      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('📱 PWA: User accepted the install prompt');
        localStorage.setItem('pwa-install-dismissed', 'installed');
      } else {
        console.log('📱 PWA: User dismissed the install prompt');
        localStorage.setItem('pwa-install-dismissed', 'maybe-later');
        localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('📱 PWA: Error during install:', error);
    }
  };

  // Handle "Maybe Later" button
  const handleMaybeLater = () => {
    localStorage.setItem('pwa-install-dismissed', 'maybe-later');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    setShowPrompt(false);
  };

  // Handle iOS install (show instructions)
  const handleIOSInstall = () => {
    // We'll show the instructions modal for iOS
    // This doesn't actually install, just shows how to do it manually
    localStorage.setItem('pwa-install-dismissed', 'maybe-later');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't render if we shouldn't show the prompt
  if (!showPrompt) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4 pwa-backdrop-enter">
        {/* Install Prompt Modal */}
        <div className="bg-white rounded-t-xl rounded-b-xl max-w-sm w-full mx-auto shadow-2xl pwa-install-enter pwa-install-modal">
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#00C5CB'}}>
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Add Fortress Asset App
                  </h3>
                  <p className="text-sm text-gray-500">to your Home Screen</p>
                </div>
              </div>
              <button
                onClick={handleMaybeLater}
                className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-6">
              Quick access, better performance, and offline support. Get the full app experience!
            </p>

            {/* iOS Instructions */}
            {isIOS && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 ios-pwa-instructions">
                <p className="text-sm font-medium text-blue-900 mb-3">How to install:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center text-xs font-bold text-blue-900">1</span>
                    <span>Tap the</span>
                    <Share size={16} className="text-blue-600" />
                    <span>Share button below</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center text-xs font-bold text-blue-900">2</span>
                    <span>Select</span>
                    <Plus size={16} className="text-blue-600" />
                    <span>"Add to Home Screen"</span>
                  </div>
                </div>
              </div>
            )}

            {/* Android Instructions */}
            {isAndroid && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <Download size={16} className="text-green-600" />
                  <span>Install directly to your home screen with one tap!</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <button
                onClick={handleMaybeLater}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={isIOS ? handleIOSInstall : handleAndroidInstall}
                className="flex-1 px-4 py-3 text-white rounded-lg hover:opacity-90 font-medium flex items-center justify-center gap-2 transition-opacity"
                style={{backgroundColor: '#00C5CB'}}
              >
                {isIOS ? (
                  <>
                    <Share size={16} />
                    Got It
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Add Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PWAInstallPrompt;